import { EditOutlined, LeftOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Drawer,
  Radio,
  Row,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import NotesSection from "../components/NotesSection";
import RequestChangeLogs from "../components/RequestChangeLogs";
import entitlements from "../config/entitlements";
import {
  ASSIGN_NOTE_TO_FIXED_INCOME_REQUEST,
  CALCULATE_FIXED_INCOME_ALLOCATION_BREAKDOWN,
  CREATE_FIXED_INCOME_PAYMENT,
  CREATE_FIXED_INCOME_PAYMENT_INSTRUCTION,
  CREATE_FIXED_INCOME_REDEMPTION,
  CREATE_FIXED_INCOME_REQUEST_KYC,
  CREATE_FIXED_INCOME_REQUEST_NOTE_TICKET,
  GET_FIXED_INCOME_REQUEST,
  GET_FIXED_INCOME_REQUEST_LOGS,
  GET_FIXED_INCOME_REQUEST_NOTIFIER_RECORDS_QUERY,
  GET_FIXED_INCOME_REQUEST_REASONS,
  GET_KYC_ENUM_OPTIONS,
  MOVE_FIXED_INCOME_REQUEST_TO_STATE,
  UPDATE_FIXED_INCOME_PAYMENT_INSTRUCTION,
  UPDATE_FIXED_INCOME_REQUEST_KYC,
} from "../gql/fixed-income-requests";
import { CREATE_NOTE } from "../gql/notes";
import {
  ActiveDrawerContent,
  AllocationBreakdown,
  APIError,
  BranchMap,
  Changelog,
  Entitlement,
  FinancialInstitution,
  FixedIncomeRequest,
  FormFieldConfig,
  FixedIncomeRequestNote,
  MaritalStatus,
  MultipleChangelog,
  Note,
  NotificationRecord,
  FixedIncomeRequestReason,
  SelectOptionsInterface,
  Ticket,
  TransitionToNextState,
  User,
  KycEnumOptions,
  FixedIncomeRequestKycBeneficiary,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  generateStateButtonText,
  getRiskLevelDisplay,
  getStateButtonClass,
  getStateColor,
  handleValidationErrors,
  institutionNameFor,
  request,
  responseHandler,
  isTreasuryBillProduct,
} from "../helpers/utilities";
import {
  notificationColumns,
  NotificationRecordTableFactory,
} from "./NotificationRecords";
import { UploadFile } from "antd/lib/upload/interface";
import { FixedIncomeRequestKycForm } from "../components/FixedIncomeRequestKycForm";
import ModalCreator from "../components/ModalCreator";
import {
  GET_FINANCIAL_INSTITUTIONS,
  GET_MARITAL_STATUSES,
} from "../gql/general";
import { FixedIncomeRequestKycReview } from "../components/FixedIncomeRequestKycReview";
import { FixedIncomeRequestKycView } from "../components/FixedIncomeRequestKycView";
import { LoanRequestReasonUI } from "./ViewLoanRequest";
import { tabPaneStyle } from "../helpers/constants";
import { MaturityDetailsCard } from "../components/MaturityDetailsCard";
import { TreasuryBillRedemptionBreakdown } from "../components/TreasuryBillRedemptionBreakdown";
import { CalculatedBreakdownDisplay } from "../components/CalculatedBreakdownDisplay";
import { AllocationBreakdownCalculator } from "../components/AllocationBreakdownCalculator";
import { TBillPricingCalculator } from "../components/TBillPricingCalculator";
import NewBaseForm from "../components/Form/BaseForm";
import { paymentMethods } from "../helpers/constants";
import { FixedIncomeRedemptionsTable } from "../components/FixedIncomeRedemptionsTable";
import debounce from "lodash.debounce";
import { USERS_BY_ROLE_QUERY } from "../gql/users";
import { FixedIncomePaymentInstructionPanel } from "../components/FixedIncomePaymentInstructionPanel";
import { DEBOUNCE_TIME } from "../helpers/constants";

interface State {
  id: string | number;
  isLoading: boolean;
  fixedIncomeRequest?: FixedIncomeRequest;
  notes: Note[];
  currentNote?: Note;
  requestLogs: MultipleChangelog | Changelog[];
  showDrawer: boolean;
  currentRequestsToProcess: number;
  activeDrawerContent: ActiveDrawerContent;
  showCancelModal: boolean;
  showTicketForm: boolean;
  isTicketCreateLoading: boolean;
  selectedFixedIncomeNoteId: string;
  showKycModal: boolean;
  maritalStatuses: MaritalStatus[];
  ghanaCardFrontUrl?: string;
  ghanaCardBackUrl?: string;
  passportUrl?: string;
  csdOfficerSignatureUrl?: string;
  csdStampUrl?: string;
  selectedLoanNoteId: string;
  availableTransitionStates?: TransitionToNextState[];
  showReasonModal: boolean;
  selectedTransitionState: string;
  isReasonLoading: boolean;
  commentRequired: boolean;
  paymentReceiptUrl?: string;
  financialInstitutions: FinancialInstitution[];
  institutionOptions: FinancialInstitution[];
  selectedPaymentMethod?: string;
  selectedPayoutCurrency?: string;
  selectedInvestmentCurrency?: string;
  showAllocationCalculator: boolean;
  showTBillCalculator: boolean;
  calculatedBreakdown?: AllocationBreakdown;
  employmentStatus?: string;
  residentialStatus?: string;
  beneficiaryType?: string;
  selectedStatementFrequency?: string;
  showRedemptionModal: boolean;
  isRedemptionLoading: boolean;
  redemptionInstitutionOptions: FinancialInstitution[];
  selectedRedemptionPaymentMethod?: string;
  payoutFinancialInstitutionId: string;
  payoutBranches: SelectOptionsInterface[];
  branchMap: BranchMap;
  payoutInstitutionOptions: SelectOptionsInterface[];
  fixedIncomeRequestReasons: FixedIncomeRequestReason[];
  selectedReasonId?: string;
  kycEnumOptions: KycEnumOptions;
  showPaymentInstructionModal: boolean;
  isCreatingPaymentInstruction: boolean;
  selectedOfferId?: string;
  offers: (SelectOptionsInterface & { rate?: number; tenure?: number })[];
  amountsPaid: number[];
  userSearchTerm: string;
  users: User[];
  isSearchingUser: boolean;
  currentUser?: User;
  isEditingPaymentInstruction: boolean;
  paymentInstructionId?: string;
  selectedRate?: number;
  selectedTenure?: number;
  paymentInstructionReceiptUrl?: string;
  paymentInstructionTransactionType?: string;
  rateInputType: "yield" | "discount";
  computedYieldRate?: number;
  supportingDocumentUrl?: string;
  beneficiaries: FixedIncomeRequestKycBeneficiary[];
  selectedRedemptionAssignedToId?: string;
  rollOver?: boolean;
  treasuryBillRedemptionType?: string;
  treasuryBillBreakdown?: AllocationBreakdown["data"];
  treasuryBillBreakdownError?: string;
  isTreasuryBillBreakdownLoading?: boolean;
  kycDeclaration?: string;
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: FixedIncomeRequest[] }
  > {
  entitlement: Entitlement;
  user: User;
}

export default class ViewFixedIncomeRequest extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  ticketReference: RefObject<FormInstance>;
  reasonReference: RefObject<FormInstance>;
  allocationCalculatorReference: RefObject<FormInstance>;
  redemptionReference: RefObject<FormInstance>;
  paymentInstructionReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      fixedIncomeRequest: undefined,
      isLoading: true,
      id: "",
      notes: [],
      currentNote: undefined,
      requestLogs: [],
      showDrawer: false,
      currentRequestsToProcess: 0,
      activeDrawerContent: "",
      showCancelModal: false,
      showTicketForm: false,
      isTicketCreateLoading: false,
      selectedFixedIncomeNoteId: "",
      showKycModal: false,
      maritalStatuses: [],
      ghanaCardFrontUrl: undefined,
      ghanaCardBackUrl: undefined,
      passportUrl: undefined,
      selectedLoanNoteId: "",
      availableTransitionStates: [],
      showReasonModal: false,
      selectedTransitionState: "",
      isReasonLoading: false,
      commentRequired: false,
      paymentReceiptUrl: undefined,
      financialInstitutions: [],
      institutionOptions: [],
      selectedPaymentMethod: undefined,
      selectedPayoutCurrency: undefined,
      selectedInvestmentCurrency: undefined,
      showAllocationCalculator: false,
      showTBillCalculator: false,
      calculatedBreakdown: undefined,
      showRedemptionModal: false,
      isRedemptionLoading: false,
      redemptionInstitutionOptions: [],
      selectedRedemptionPaymentMethod: undefined,
      payoutFinancialInstitutionId: "",
      payoutBranches: [],
      branchMap: {},
      payoutInstitutionOptions: [],
      fixedIncomeRequestReasons: [],
      selectedReasonId: undefined,
      kycEnumOptions: {},
      showPaymentInstructionModal: false,
      isCreatingPaymentInstruction: false,
      selectedOfferId: undefined,
      offers: [],
      amountsPaid: [0],
      userSearchTerm: "",
      users: [],
      isSearchingUser: false,
      currentUser: undefined,
      isEditingPaymentInstruction: false,
      paymentInstructionId: undefined,
      selectedRate: undefined,
      selectedTenure: undefined,
      paymentInstructionReceiptUrl: undefined,
      paymentInstructionTransactionType: undefined,
      rateInputType: "yield",
      computedYieldRate: undefined,
      supportingDocumentUrl: undefined,
      selectedRedemptionAssignedToId: undefined,
      rollOver: false,
      treasuryBillRedemptionType: undefined,
      treasuryBillBreakdown: undefined,
      treasuryBillBreakdownError: undefined,
      isTreasuryBillBreakdownLoading: false,
      kycDeclaration: undefined,
      beneficiaries: [
        {
          beneficiaryFullName: "",
          beneficiaryGender: "",
          beneficiaryMaritalStatus: "",
          beneficiaryDateOfBirth: "",
          beneficiaryPlaceOfBirth: "",
          beneficiaryResidentialAddress: "",
          beneficiaryDigitalAddress: "",
          beneficiaryOccupation: "",
          beneficiaryTaxIdentificationNumber: "",
          beneficiaryMobileNumber1: "",
          beneficiaryPercentageOfBenefit: 0,
        },
      ],
    };
    this.reference = React.createRef<FormInstance>();
    this.ticketReference = React.createRef<FormInstance>();
    this.reasonReference = React.createRef<FormInstance>();
    this.allocationCalculatorReference = React.createRef<FormInstance>();
    this.redemptionReference = React.createRef<FormInstance>();
    this.paymentInstructionReference = React.createRef<FormInstance>();
  }

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        currentRequestsToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getFixedIncomeRequest();
        this.getMaritalStatuses();
        this.getFinancialInstitutions();
        this.getKycEnumOptions();
        this.getAdvisors();
      },
    );
  }

  getFinancialInstitutions = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
      });

      this.generateBranchMap();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getFixedIncomeRequest = (): void => {
    const { id } = this.state;
    interface Response {
      fixedIncomeRequest: FixedIncomeRequest;
    }

    this.setState({ isLoading: true });
    request({
      query: GET_FIXED_INCOME_REQUEST,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        const { fixedIncomeRequest } = data.data;
        this.setState({
          fixedIncomeRequest: fixedIncomeRequest,
          availableTransitionStates:
            fixedIncomeRequest?.canTransitionToNextStates,
          employmentStatus:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.employmentStatus,
          residentialStatus:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.residentialStatus,
          beneficiaryType:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.beneficiaries?.[0]
              ?.beneficiaryType,
          selectedStatementFrequency:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.statementFrequency,
          notes:
            fixedIncomeRequest?.fixedIncomeRequestNotes?.map(
              (fixedIncomeRequestNote: FixedIncomeRequestNote) => {
                return {
                  ...fixedIncomeRequestNote.note,
                  referenceId: fixedIncomeRequestNote.id,
                };
              },
            ) || [],
          beneficiaries:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.beneficiaries || [],
          kycDeclaration:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.kycDeclaration ||
            undefined,
        });
      })
      .catch((error) => {
        message.error("Failed to load fixed income request");
        Sentry.captureException(error);
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  };

  getFixedIncomeRequestLogs = (): void => {
    const { fixedIncomeRequest } = this.state;
    interface Response {
      fixedIncomeRequestChangeLogs: MultipleChangelog;
    }

    this.setState({ isLoading: true });
    request({
      query: GET_FIXED_INCOME_REQUEST_LOGS,
      variables: { fixedIncomeRequestId: fixedIncomeRequest?.id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        this.setState({
          requestLogs: data?.data?.fixedIncomeRequestChangeLogs,
        });
      })
      .catch((error) => {
        message.error("Failed to load request logs");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  getRequestNotifierRecords = (): void => {
    const { id: fixedIncomeRequestId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_FIXED_INCOME_REQUEST_NOTIFIER_RECORDS_QUERY,
      variables: { fixedIncomeRequestId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            fixedIncomeRequest: FixedIncomeRequest;
          };
        }>): void => {
          this.setState({
            fixedIncomeRequest: {
              ...this.state.fixedIncomeRequest,
              ...data.data.fixedIncomeRequest,
            },
          });
        },
      )
      .catch((error) => {
        message.error("Failed to load notification records");
        Sentry.captureException(error);
      })
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  createNote = async (): Promise<void> => {
    const { user } = this.props;

    interface Response {
      fixedIncomeRequestNote: Note;
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createNote",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.setState({ isLoading: false });
        return;
      }

      if (mutation.note) {
        await this.assignNoteToFixedIncomeRequest(mutation.note.id);
        message.success("Note created successfully");
      }

      this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      message.error(
        `Failed to create note - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  assignNoteToFixedIncomeRequest = (noteId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignNoteToFixedIncomeRequest: {
          fixedIncomeRequestNote: {
            id: string;
            note: Note;
          };
        };
      };
    }

    return request({
      query: ASSIGN_NOTE_TO_FIXED_INCOME_REQUEST,
      variables: { noteId: noteId, fixedIncomeRequestId: this.state.id },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        let newNote = {
          ...data.assignNoteToFixedIncomeRequest.fixedIncomeRequestNote.note,
          referenceId:
            data.assignNoteToFixedIncomeRequest.fixedIncomeRequestNote.id,
        };

        this.setState({
          isLoading: false,
          currentNote: newNote,
          notes: [...this.state.notes, newNote],
        });
      })
      .catch((error) => {
        message.error("Failed to assign note");
        Sentry.captureException(error);
        this.setState({ isLoading: false });
      });
  };

  handleNoteUpdate = (updatedNotes: Note[]): void => {
    this.setState({ notes: updatedNotes });
  };

  toggleCreateTicket = (id: string): void => {
    this.setState((oldState) => ({
      selectedFixedIncomeNoteId: id,
      showTicketForm: !oldState.showTicketForm,
      currentNote: this.state.notes.find((note) => note.referenceId === id),
    }));
  };

  createTicket = async (): Promise<void> => {
    const { selectedFixedIncomeNoteId, fixedIncomeRequest } = this.state;
    const fieldsToBeValidated = [
      "title",
      "description",
      "email",
      "firstName",
      "lastName",
      "source",
      "summary",
      "issue_type",
      "project",
      "mentions",
      "ticketSubCategoryId",
      "type",
      "assigneeId",
    ];

    interface Response {
      ticket: Ticket;
    }

    if (!this.ticketReference.current) return;

    let data;

    try {
      data = await this.ticketReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isTicketCreateLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_FIXED_INCOME_REQUEST_NOTE_TICKET,
        variables: {
          fixedIncomeRequestNoteId: selectedFixedIncomeNoteId,
          properties: {
            ...data,
            first_name: data.firstName,
            last_name: data.lastName,
            system_user: this.props.user.email,
            title: `Fixed Income Request - ${fixedIncomeRequest?.customer?.firstName} ${fixedIncomeRequest?.customer?.lastName}`,
            group: "Users",
            priority: "2 normal",
            state: "open",
            petra_id: fixedIncomeRequest?.customer?.petraId,
            assigneeId: data.assigneeId,
          },
          source: data.source,
          ticketSubCategoryId: data.ticketSubCategoryId,
        },
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createFixedIncomeRequestNoteTicket",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        let currentNote = this.state.currentNote?.tickets
          ? ({
              ...this.state.currentNote,
              tickets: [this.state.currentNote?.tickets, mutation.ticket],
            } as unknown as Note)
          : ({
              ...this.state.currentNote,
              tickets: [mutation.ticket],
            } as unknown as Note);

        this.getFixedIncomeRequest();
        this.ticketReference.current.resetFields(fieldsToBeValidated);
        this.setState((oldState) => ({
          showTicketForm: !oldState.showTicketForm,
          notes: [...this.state.notes, currentNote],
        }));
        message.success("Ticket Created Successfully");
      }
    } catch (error) {
      message.error(
        `Could Not Create Ticket - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
    }
    this.setState({ isTicketCreateLoading: false });
  };

  executeDrawerQuery = (currentDrawer: ActiveDrawerContent): void => {
    switch (currentDrawer) {
      case "notifierRecords":
        this.getRequestNotifierRecords();
        break;
      case "audits":
        this.getFixedIncomeRequestLogs();
        break;
      default:
        break;
    }
  };

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };

  getDrawerContent = (): JSX.Element => {
    const { requestLogs, activeDrawerContent, fixedIncomeRequest, isLoading } =
      this.state;

    switch (activeDrawerContent) {
      case "audits":
        return <RequestChangeLogs data={requestLogs} isLoading={isLoading} />;
      case "notifierRecords":
        const records = fixedIncomeRequest?.notifierRecords as
          | NotificationRecord[]
          | undefined;
        return (
          <Fragment>
            {records && records.length > 0 ? (
              NotificationRecordTableFactory(records, notificationColumns)
            ) : (
              <p style={{ textAlign: "center", color: "#999" }}>
                No notification records found
              </p>
            )}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Fixed Income Request Logs";
      case "notifierRecords":
        return "Notification Records";
      default:
        return "Entity";
    }
  };

  toggleKycModal = (): void => {
    this.setState((prevState) => ({
      showKycModal: !prevState.showKycModal,
    }));
  };

  handleKycSubmit = async (kycData: any): Promise<void> => {
    interface Response {
      fixedIncomeRequestKyc: any;
      errors: APIError[];
    }

    if (this.state.beneficiaryType === "beneficiary") {
      const totalPercentage = this.state.beneficiaries.reduce(
        (sum, b) => sum + (b.beneficiaryPercentageOfBenefit || 0),
        0,
      );

      if (Math.abs(totalPercentage - 100) > 0.01) {
        message.error(
          `Beneficiary percentages must sum to 100%. Current total: ${totalPercentage}%`,
        );
        return;
      }
    }

    this.setState({ isLoading: true });

    try {
      const { fixedIncomeRequest } = this.state;
      const isEditMode = !!fixedIncomeRequest?.fixedIncomeRequestKyc;

      const mutation = isEditMode
        ? UPDATE_FIXED_INCOME_REQUEST_KYC
        : CREATE_FIXED_INCOME_REQUEST_KYC;
      const mutationName = isEditMode
        ? "updateFixedIncomeRequestKyc"
        : "createFixedIncomeRequestKyc";

      const {
        ghanaCardFrontUrl,
        ghanaCardBackUrl,
        passportUrl,
        csdOfficerSignatureUrl,
        csdStampUrl,
        kycDeclaration,
      } = this.state;

      const filteredKycData = Object.keys(kycData).reduce((acc, key) => {
        if (/_\d+$/.test(key)) {
          return acc;
        }
        acc[key] = kycData[key];
        return acc;
      }, {} as any);

      // Construct beneficiaries array with null for empty dates
      const beneficiaries = this.state.beneficiaries.map((beneficiary) => ({
        beneficiaryType: this.state.beneficiaryType,
        beneficiaryTitle: beneficiary.beneficiaryTitle || null,
        beneficiaryFullName: beneficiary.beneficiaryFullName,
        beneficiaryGender: beneficiary.beneficiaryGender,
        beneficiaryMaritalStatus: beneficiary.beneficiaryMaritalStatus || null,
        beneficiaryRelationship: beneficiary.beneficiaryRelationship || null,
        beneficiaryDateOfBirth: beneficiary.beneficiaryDateOfBirth || null,
        beneficiaryPlaceOfBirth: beneficiary.beneficiaryPlaceOfBirth,
        beneficiaryResidentialStatus:
          beneficiary.beneficiaryResidentialStatus || null,
        beneficiaryCountryOfOrigin:
          beneficiary.beneficiaryCountryOfOrigin || null,
        beneficiaryCountryOfResidence:
          beneficiary.beneficiaryCountryOfResidence || null,
        beneficiaryResidentialAddress:
          beneficiary.beneficiaryResidentialAddress,
        beneficiaryPercentageOfBenefit:
          this.state.beneficiaryType === "beneficiary"
            ? beneficiary.beneficiaryPercentageOfBenefit
            : null,
        beneficiaryDigitalAddress: beneficiary.beneficiaryDigitalAddress,
        beneficiaryOccupation: beneficiary.beneficiaryOccupation,
        beneficiaryTaxIdentificationNumber:
          beneficiary.beneficiaryTaxIdentificationNumber,
        beneficiaryMobileNumber1: beneficiary.beneficiaryMobileNumber1,
        beneficiaryMobileNumber2: beneficiary.beneficiaryMobileNumber2 || null,
        beneficiaryGhanaCardUrl:
          this.state.beneficiaryType === "in_trust_for"
            ? beneficiary.beneficiaryGhanaCardUrl || null
            : null,
        beneficiaryPassportUrl:
          this.state.beneficiaryType === "in_trust_for"
            ? beneficiary.beneficiaryPassportUrl || null
            : null,
      }));

      let variables;
      if (isEditMode) {
        const { fixedIncomeRequestId, customerId, ...updateData } =
          filteredKycData;
        variables = {
          id: fixedIncomeRequest?.fixedIncomeRequestKyc?.id,
          ...updateData,
          ghanaCardFrontUrl,
          ghanaCardBackUrl,
          passportUrl,
          csdOfficerSignatureUrl,
          csdStampUrl,
          beneficiaries,
          kycDeclaration,
        };
      } else {
        variables = {
          ...filteredKycData,
          ghanaCardFrontUrl:
            ghanaCardFrontUrl || fixedIncomeRequest?.customer?.ghanaCardIdUrl,
          ghanaCardBackUrl:
            ghanaCardBackUrl ||
            fixedIncomeRequest?.customer?.ghanaCardIdBackUrl,
          passportUrl,
          csdOfficerSignatureUrl,
          csdStampUrl,
          beneficiaries,
          kycDeclaration,
        };
      }

      const response: AxiosResponse = await request({
        query: mutation,
        variables,
      });

      if (response.data.errors && response.data.errors.length > 0) {
        message.error(generateDisplayErrorMessage(response.data.errors));
        this.setState({ isLoading: false });
        return;
      }

      const { err, mutation: mutationResult } = responseHandler<Response>(
        response,
        mutationName,
      );

      if (err || mutationResult.errors?.length) {
        message.error(
          generateDisplayErrorMessage(err || mutationResult.errors),
        );
      } else {
        message.success(
          isEditMode
            ? "KYC updated successfully!"
            : "KYC submitted successfully!",
        );
        this.setState({ showKycModal: false });
        this.getFixedIncomeRequest();
      }
    } catch (error) {
      message.error(
        this.state.fixedIncomeRequest?.fixedIncomeRequestKyc
          ? "Failed to update KYC"
          : "Failed to submit KYC",
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleEmploymentStatusChange = (value: string) => {
    this.setState({ employmentStatus: value });
  };

  handleResidentialStatusSelect = (value: string) => {
    this.setState({ residentialStatus: value });
  };

  handleBeneficiaryTypeChange = (value: string) => {
    this.setState({ beneficiaryType: value });
  };

  handleStatementFrequencyChange = (value: string) => {
    console.log(value);
    this.setState({ selectedStatementFrequency: value });
  };

  handleKycDeclarationChange = (value: string) => {
    this.setState({ kycDeclaration: value });
  };

  handleDelete = (file: UploadFile): void => {
    let newState = {};

    switch (file.name) {
      case "Ghana Card (Front)":
        newState = {
          ghanaCardIdUrl: "",
          ghanaCardFrontUrl: "",
        };
        break;
      case "Ghana Card (Back)":
        newState = { ghanaCardIdBackUrl: "", ghanaCardBackUrl: "" };
        break;
      case "Passport Picture":
        newState = { passportUrl: "" };
        break;
      case "CSD Officer Signature":
        newState = { csdOfficerSignatureUrl: "" };
        break;
      case "CSD Stamp":
        newState = { csdStampUrl: "" };
        break;
      default:
        break;
    }

    this.setState(newState);
  };

  getMaritalStatuses = (): void => {
    request({
      query: GET_MARITAL_STATUSES,
    })
      .then(({ data }: AxiosResponse<any>): void => {
        this.setState({
          maritalStatuses: data.data.maritalStatuses || [],
        });
      })
      .catch(() => {});
  };

  handleReasonSelect = (reasonId: string): void => {
    this.setState({
      selectedReasonId: reasonId,
    });
  };

  saveStateReason = async (): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    const { selectedTransitionState, availableTransitionStates } = this.state;

    let data;

    if (!this.reasonReference.current) return;

    const selectedTransition = availableTransitionStates?.find(
      (t) => t.state === selectedTransitionState,
    );
    const requiresDisbursement =
      selectedTransition?.complete_disbursement || false;
    const requiresPayment = selectedTransition?.create_payment || false;
    const issuedDateRequired =
      selectedTransition?.issued_date_required || false;
    const requiresReason = selectedTransition?.reason_required || false;

    const fieldsToBeValidated = ["comment"];

    if (requiresReason) {
      fieldsToBeValidated.push("fixedIncomeRequestReasonId");
    }

    if (requiresPayment) {
      fieldsToBeValidated.push(
        "paymentAmount",
        "paymentDate",
        "paymentReceiptUrl",
        "accountName",
        "accountNumber",
        "bankName",
        "paymentMethod",
      );
    }

    if (requiresDisbursement) {
      fieldsToBeValidated.push("disbursedAmount", "disbursedAt");
    }

    if (issuedDateRequired) {
      fieldsToBeValidated.push("issuedDate");
      if (this.isTreasuryBill()) {
        fieldsToBeValidated.push("isin");
      }
    }

    try {
      data = await this.reasonReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isReasonLoading: true });

    try {
      if (requiresPayment) {
        await this.createFixedIncomePayment(
          data.paymentAmount,
          data.paymentDate,
          this.state.paymentReceiptUrl,
          data.accountName,
          data.accountNumber,
          data.bankName,
          data.paymentMethod,
        );
        this.getFixedIncomeRequest();
      } else {
        await this.moveToNextState(data, selectedTransition);
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(
        `Failed to process state transition - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
    } finally {
      this.reasonReference.current.resetFields();
      this.setState({
        isReasonLoading: false,
        showReasonModal: false,
        paymentReceiptUrl: undefined,
        selectedReasonId: undefined,
        fixedIncomeRequestReasons: [],
      });
    }
  };

  createFixedIncomePayment = async (
    amount: number,
    paymentDate: string,
    paymentReceiptUrl?: string,
    accountName?: string,
    accountNumber?: string,
    bankName?: string,
    paymentMethod?: string,
  ): Promise<void> => {
    interface PaymentResponse {
      fixedIncomePayment: any;
      errors: APIError[];
    }

    const response: AxiosResponse = await request({
      query: CREATE_FIXED_INCOME_PAYMENT,
      variables: {
        amount,
        paymentDate,
        paymentReceiptUrl,
        fixedIncomeRequestId: this.state.id,
        accountName,
        accountNumber,
        bankName,
        paymentMethod,
      },
    });

    const { err, mutation } = responseHandler<PaymentResponse>(
      response,
      "createFixedIncomePayment",
    );

    if (err || mutation.errors?.length) {
      message.error(generateDisplayErrorMessage(err || mutation.errors));
      return;
    }

    message.success("Payment recorded successfully and request state updated");
  };

  moveToNextState = async (
    data: any,
    transition: TransitionToNextState | undefined,
  ): Promise<void> => {
    interface Response {
      fixedIncomeRequest: FixedIncomeRequest;
      errors: APIError[];
    }

    const variables: any = {
      fixedIncomeRequestId: this.state.id,
      comment: data.comment,
      state: this.state.selectedTransitionState,
      event: transition?.event,
    };

    if (transition?.reason_required && data.fixedIncomeRequestReasonId) {
      variables.fixedIncomeRequestReasonId = data.fixedIncomeRequestReasonId;
    }

    if (transition?.complete_disbursement) {
      if (data.disbursedAmount) {
        variables.disbursedAmount = data.disbursedAmount;
      }
      if (data.disbursedAt) {
        variables.disbursedAt = data.disbursedAt;
      }
    }

    if (transition?.issued_date_required) {
      if (data.issuedDate) {
        variables.issuedDate = data.issuedDate;
      }
      if (data.isin) {
        variables.isin = data.isin;
      }
    }

    const response: AxiosResponse = await request({
      query: MOVE_FIXED_INCOME_REQUEST_TO_STATE,
      variables,
    });

    const { err, mutation } = responseHandler<Response>(
      response,
      "moveFixedIncomeRequestToState",
    );

    const error = err || mutation.errors;

    if (mutation.fixedIncomeRequest) {
      const newState = mutation.fixedIncomeRequest.state;
      const stateMessages: Record<string, string> = {
        pending_kyc: "Fixed income request moved to pending KYC",
        pending_approval: "Fixed income request is pending approval",
        approved: "Fixed income request has been approved",
        pending_payment_verification:
          "Fixed income request is pending payment verification",
        pending_compliance_approval:
          "Fixed income request is pending compliance approval",
        payment_verified: "Payment has been verified successfully",
        cancelled: "Fixed income request has been cancelled",
        hold: "Fixed income request has been put on hold",
        purchased: "Fixed income request has been purchased successfully",
      };

      const successMessage = newState
        ? stateMessages[newState] ||
          `Fixed income request state updated to ${formatSnakeCase(newState)}`
        : "Fixed income request state updated successfully";

      message.success(successMessage);
      this.getFixedIncomeRequest();
    } else if (error) {
      message.error(generateDisplayErrorMessage(error));
    } else {
      throw new Error(generateDisplayErrorMessage(error));
    }
  };

  toggleReasonModal = (): void => {
    this.setState((prevState) => ({
      showReasonModal: !prevState.showReasonModal,
    }));
  };

  handlePaymentMethodSelect = (paymentMethod: string): void => {
    let filteredInstitutions: FinancialInstitution[] = [];

    if (paymentMethod === "mobile_money") {
      filteredInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else if (paymentMethod === "bank_transfer") {
      filteredInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    } else {
      filteredInstitutions = this.state.financialInstitutions;
    }

    this.setState({
      institutionOptions: filteredInstitutions,
      selectedPaymentMethod: paymentMethod,
    });
  };

  toggleTBillCalculator = (): void => {
    this.setState((prevState) => ({
      showTBillCalculator: !prevState.showTBillCalculator,
    }));
  };

  toggleAllocationCalculator = (): void => {
    this.setState((prevState) => ({
      showAllocationCalculator: !prevState.showAllocationCalculator,
      calculatedBreakdown: undefined,
    }));
  };

  calculateAllocationBreakdown = async (): Promise<void> => {
    if (!this.allocationCalculatorReference.current) return;

    const data = this.allocationCalculatorReference.current.getFieldsValue();

    this.setState({ isLoading: true });

    try {
      const variables: any = {
        id: this.state.id,
      };

      if (data.amount && data.amount > 0) {
        variables.amount = data.amount;
      }

      if (data.rediscountDate) {
        variables.rediscountDate = moment(data.rediscountDate).format(
          "YYYY-MM-DD",
        );
      }

      const response: AxiosResponse = await request({
        query: CALCULATE_FIXED_INCOME_ALLOCATION_BREAKDOWN,
        variables,
      });

      const breakdown =
        response.data.data.calculateFixedIncomeAllocationBreakdown;

      if (breakdown && breakdown.success && breakdown.data) {
        this.setState({
          calculatedBreakdown: breakdown,
        });
        message.success("Allocation breakdown calculated successfully");
      } else {
        message.error("Failed to calculate allocation breakdown");
        message.error(breakdown.error);

        this.setState({ calculatedBreakdown: undefined });
      }
    } catch (error) {
      console.error("Calculation error:", error);
      message.error(
        `Failed to calculate allocation breakdown - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      Sentry.captureException(error);
      this.setState({ calculatedBreakdown: undefined });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleRedemptionModal = (): void => {
    this.cancelTreasuryBillBreakdown();
    this.setState((prevState) => ({
      showRedemptionModal: !prevState.showRedemptionModal,
      treasuryBillRedemptionType: undefined,
      treasuryBillBreakdown: undefined,
      treasuryBillBreakdownError: undefined,
    }));
  };

  isTreasuryBill = (): boolean =>
    isTreasuryBillProduct(this.state.fixedIncomeRequest?.product);

  handleTreasuryBillRedemptionTypeSelect = (redemptionType: string): void => {
    this.setState({ treasuryBillRedemptionType: redemptionType });
    if (redemptionType === "partial") {
      this.redemptionReference.current?.setFieldsValue({ amount: undefined });
    }
    // onSelect can fire before the form stores the new value, so read it after a moment.
    this.scheduleTreasuryBillBreakdown();
  };

  // Recalculating on every keystroke would flood the API, so changes are batched.
  treasuryBillBreakdownTimer?: ReturnType<typeof setTimeout>;
  // Only the latest calculation may touch the form; anything older is ignored.
  treasuryBillBreakdownRequestId = 0;

  cancelTreasuryBillBreakdown = (): void => {
    if (this.treasuryBillBreakdownTimer) {
      clearTimeout(this.treasuryBillBreakdownTimer);
    }
    this.treasuryBillBreakdownRequestId += 1;
    this.setState({ isTreasuryBillBreakdownLoading: false });
  };

  scheduleTreasuryBillBreakdown = (): void => {
    this.cancelTreasuryBillBreakdown();
    this.treasuryBillBreakdownTimer = setTimeout(
      this.refreshTreasuryBillBreakdown,
      400,
    );
  };

  // Works out the charges and balance for what is in the form, so they can be checked
  // before the redemption is created. A full redemption's amount is calculated and locked.
  refreshTreasuryBillBreakdown = async (): Promise<void> => {
    const form = this.redemptionReference.current;
    if (!form) return;

    const { redemptionType, serviceCostRate, amount, rediscountDate } =
      form.getFieldsValue([
        "redemptionType",
        "serviceCostRate",
        "amount",
        "rediscountDate",
      ]);

    if (!redemptionType) return;
    const isFull = redemptionType === "full";

    // Without an amount the API would work out a redemption of the whole balance.
    if (!isFull && !(Number(amount) > 0)) {
      this.setState({
        treasuryBillBreakdown: undefined,
        treasuryBillBreakdownError: undefined,
      });
      return;
    }

    this.treasuryBillBreakdownRequestId += 1;
    const requestId = this.treasuryBillBreakdownRequestId;
    const isLatestRequest = () =>
      requestId === this.treasuryBillBreakdownRequestId;

    this.setState({ isTreasuryBillBreakdownLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CALCULATE_FIXED_INCOME_ALLOCATION_BREAKDOWN,
        variables: {
          id: this.state.id,
          redemptionType,
          serviceCostRate:
            serviceCostRate === undefined || serviceCostRate === null
              ? undefined
              : Number(serviceCostRate),
          amount: isFull ? undefined : Number(amount),
          rediscountDate: rediscountDate
            ? moment(rediscountDate).format("YYYY-MM-DD")
            : undefined,
        },
      });

      if (!isLatestRequest()) return;

      const breakdown =
        response.data.data.calculateFixedIncomeAllocationBreakdown;

      if (breakdown?.success && breakdown.data) {
        this.setState({
          treasuryBillBreakdown: breakdown.data,
          treasuryBillBreakdownError: undefined,
        });
        if (isFull) {
          form.setFieldsValue({
            amount: Number(
              breakdown.data.redemption_data.total_redemption_amount,
            ),
          });
        }
      } else {
        this.setState({
          treasuryBillBreakdown: undefined,
          treasuryBillBreakdownError:
            breakdown?.error || "Unable to calculate the redemption",
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      if (!isLatestRequest()) return;
      this.setState({
        treasuryBillBreakdown: undefined,
        treasuryBillBreakdownError: generateDisplayErrorMessage(error),
      });
    } finally {
      if (isLatestRequest()) {
        this.setState({ isTreasuryBillBreakdownLoading: false });
      }
    }
  };

  changeRollOver = async (checkedValue: boolean) => {
    this.setState({
      rollOver: checkedValue,
    });
  };

  handleRedemptionSubmit = async (): Promise<void> => {
    if (!this.redemptionReference.current) return;

    interface Response {
      fixedIncomeRedemption: any;
      errors: APIError[];
    }

    const isTreasuryBill = this.isTreasuryBill();

    const fieldsToValidate = [
      "amount",
      "rediscountDate",
      "accountName",
      "accountNumber",
      "bankName",
      "paymentMethod",
      "supportingDocumentUrl",
    ];

    if (isTreasuryBill) {
      fieldsToValidate.push("redemptionType", "serviceCostRate");
    } else {
      fieldsToValidate.push("assignedToId", "rollOver");
    }

    let data;

    try {
      data = await this.redemptionReference.current.validateFields(
        fieldsToValidate,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isRedemptionLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_FIXED_INCOME_REDEMPTION,
        variables: {
          amount: data.amount,
          fixedIncomeRequestId: this.state.id,
          rediscountDate: data.rediscountDate
            ? moment(data.rediscountDate).format("YYYY-MM-DD")
            : undefined,
          accountName: data.accountName,
          accountNumber: data.accountNumber,
          bankName: data.bankName,
          paymentMethod: data.paymentMethod,
          assignedToId: data.assignedToId,
          rollOver: isTreasuryBill ? false : this.state.rollOver,
          supportingDocumentUrl: this.state.supportingDocumentUrl,
          redemptionType: isTreasuryBill ? data.redemptionType : undefined,
          serviceCostRate:
            isTreasuryBill && data.serviceCostRate != null
              ? Number(data.serviceCostRate)
              : undefined,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createFixedIncomeRedemption",
      );

      if (err || mutation.errors?.length) {
        message.error(generateDisplayErrorMessage(err || mutation.errors));
        this.setState({ isRedemptionLoading: false });
        return;
      }

      message.success("Redemption created successfully!");

      this.cancelTreasuryBillBreakdown();
      this.setState({
        showRedemptionModal: false,
        selectedRedemptionPaymentMethod: undefined,
        redemptionInstitutionOptions: [],
        isRedemptionLoading: false,
        treasuryBillRedemptionType: undefined,
        treasuryBillBreakdown: undefined,
        treasuryBillBreakdownError: undefined,
      });

      if (this.redemptionReference.current) {
        this.redemptionReference.current.resetFields();
      }

      this.getFixedIncomeRequest();
    } catch (error) {
      message.error(
        `Failed to create redemption - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
      this.setState({ isRedemptionLoading: false });
    }
  };

  handleRedemptionPaymentMethodSelect = (paymentMethod: string): void => {
    let filteredInstitutions: FinancialInstitution[] = [];

    if (paymentMethod === "mobile_money") {
      filteredInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else if (paymentMethod === "bank_transfer") {
      filteredInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    } else {
      filteredInstitutions = this.state.financialInstitutions;
    }

    this.setState({
      redemptionInstitutionOptions: filteredInstitutions,
      selectedRedemptionPaymentMethod: paymentMethod,
    });
  };

  requestForReason = async (state: string): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    const selectedTransition = this.state.availableTransitionStates?.find(
      (t) => t.state === state,
    );

    if (selectedTransition?.create_payment_instruction) {
      this.togglePaymentInstructionModal();
      return;
    }

    if (selectedTransition?.reason_required) {
      interface Response {
        fixedIncomeRequestReasons: FixedIncomeRequestReason[];
        errors: APIError[];
      }

      this.setState({ isReasonLoading: true });

      try {
        request({
          query: GET_FIXED_INCOME_REQUEST_REASONS,
          variables: { state },
        })
          .then(({ data }: AxiosResponse<{ data: Response }>) => {
            this.setState({
              fixedIncomeRequestReasons: data.data.fixedIncomeRequestReasons,
              showReasonModal: true,
              selectedTransitionState: state,
            });
          })
          .finally(() => {
            this.setState({ isReasonLoading: false });
          });
      } catch (error) {
        message.error(
          `An error occurred, please try again later - ${generateDisplayErrorMessage(
            error,
          )}`,
        );
        Sentry.captureException(error);
        this.setState({ isReasonLoading: false });
      }
    } else {
      this.setState({
        showReasonModal: true,
        selectedTransitionState: state,
        fixedIncomeRequestReasons: [],
      });
    }
  };

  handlePayoutFinancialInstitutionChange = (
    payoutFinancialInstitutionId: string,
  ): void => {
    const branches = this.state.branchMap?.[payoutFinancialInstitutionId] || [];
    this.setState({
      payoutFinancialInstitutionId,
      payoutBranches: branches,
    });
  };

  generateBranchMap = (): void => {
    const branchMap: BranchMap = {};

    this.state.financialInstitutions.forEach(
      (institution: FinancialInstitution): void => {
        branchMap[institution.id] = institution.bankBranches.map((branch) => ({
          ...branch,
          value: branch.name,
        }));
      },
    );

    this.setState({ branchMap });
  };

  handlePayoutPaymentMethodSelect = (paymentMethod: string): void => {
    let filteredInstitutions: FinancialInstitution[] = [];

    if (paymentMethod === "mobile_money") {
      filteredInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else if (paymentMethod === "bank_transfer") {
      filteredInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    } else {
      filteredInstitutions = this.state.financialInstitutions;
    }

    this.setState({
      payoutInstitutionOptions: filteredInstitutions,
      selectedPaymentMethod: paymentMethod,
    });
  };

  handleKycPayoutBankSelect = (bankName: string): void => {
    const institution = this.state.financialInstitutions.find(
      (i) => i.name === bankName,
    );
    const branches = institution
      ? this.state.branchMap[institution.id] || []
      : [];
    this.setState({ payoutBranches: branches });
  };

  handlePayoutCurrencySelect = (currency: string): void => {
    this.setState({
      selectedPayoutCurrency: currency,
    });
  };

  handleInvestmentCurrencySelect = (currency: string): void => {
    this.setState({
      selectedInvestmentCurrency: currency,
    });
  };

  getKycEnumOptions = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse = await request({
        query: GET_KYC_ENUM_OPTIONS,
      });

      this.setState({
        kycEnumOptions: data.data.kycEnumOptions || {},
      });
    } catch (error) {
      console.error("Failed to load KYC enum options:", error);
      Sentry.captureException(error);
    }
  };

  getAdvisors = (): void => {
    request({
      query: USERS_BY_ROLE_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
        role: "Advisors",
      },
    })
      .then(({ data: { data } }: any): void => {
        const users = data.users.results;

        const { currentUser } = this.state;
        if (currentUser && !users.find((u: User) => u.id === currentUser.id)) {
          users.unshift(currentUser);
        }

        this.setState({ users, isSearchingUser: false });
      })
      .catch((error) => {
        this.setState({ isSearchingUser: false });
        Sentry.captureException(error);
      });
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm, isSearchingUser: true }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAdvisors();
  }, DEBOUNCE_TIME);

  togglePaymentInstructionModal = (isEdit: boolean = false): void => {
    const { fixedIncomeRequest, showPaymentInstructionModal } = this.state;

    if (!showPaymentInstructionModal) {
      const product = fixedIncomeRequest?.product;
      const offers = (product?.fixedIncomeOffers || [])
        .filter((offer: any) => offer.isActive)
        .map((offer: any) => ({
          id: offer.id,
          name: `${offer.rate}% - ${offer.tenure} days`,
          value: offer.id,
          label: `${offer.rate}% - ${offer.tenure} days`,
          rate: offer.rate,
          tenure: offer.tenure,
        }));

      if (isEdit && fixedIncomeRequest?.fixedIncomePaymentInstruction) {
        const pi = fixedIncomeRequest.fixedIncomePaymentInstruction;
        this.setState(
          {
            offers,
            isEditingPaymentInstruction: true,
            paymentInstructionId: pi.id,
            amountsPaid: pi.amountsPaid || [0],
            selectedRate: pi.rate,
            selectedTenure: pi.tenure,
            paymentInstructionReceiptUrl: pi.receiptUrl,
            paymentInstructionTransactionType: pi.transactionType || "primary",
            rateInputType: "yield",
            computedYieldRate: undefined,
            showPaymentInstructionModal: true,
            currentUser: pi.assignedTo || undefined,
          },
          () => this.getAdvisors(),
        );

        setTimeout(() => {
          this.paymentInstructionReference.current?.setFieldsValue({
            assignedToId: pi.assignedTo?.id,
            amountsPaid: pi.amountsPaid || [0],
            rate: pi.rate,
            tenure: pi.tenure,
            transactionType: pi.transactionType || "primary",
          });
        }, 0);
        return;
      } else {
        this.setState({
          offers,
          isEditingPaymentInstruction: false,
          showPaymentInstructionModal: true,
          selectedRate: undefined,
          selectedTenure: undefined,
          amountsPaid: [0],
          paymentInstructionId: undefined,
          paymentInstructionReceiptUrl: undefined,
          paymentInstructionTransactionType: undefined,
          rateInputType: "yield",
          computedYieldRate: undefined,
        });
        return;
      }
    }

    this.setState({
      showPaymentInstructionModal: false,
      selectedRate: undefined,
      selectedTenure: undefined,
      amountsPaid: [0],
      isEditingPaymentInstruction: false,
      paymentInstructionId: undefined,
      paymentInstructionReceiptUrl: undefined,
      paymentInstructionTransactionType: undefined,
      rateInputType: "yield",
      computedYieldRate: undefined,
    });
  };

  createPaymentInstruction = async (): Promise<void> => {
    if (!this.paymentInstructionReference.current) return;

    interface Response {
      fixedIncomePaymentInstruction: any;
      errors: APIError[];
    }

    const {
      isEditingPaymentInstruction,
      paymentInstructionId,
      paymentInstructionReceiptUrl,
      paymentInstructionTransactionType,
      rateInputType,
    } = this.state;

    const fieldsToValidate = [
      "assignedToId",
      "amountsPaid",
      "rate",
      "tenure",
    ];

    let data;

    try {
      data = await this.paymentInstructionReference.current.validateFields(
        fieldsToValidate,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isCreatingPaymentInstruction: true });

    try {
      const mutation = isEditingPaymentInstruction
        ? UPDATE_FIXED_INCOME_PAYMENT_INSTRUCTION
        : CREATE_FIXED_INCOME_PAYMENT_INSTRUCTION;

      const mutationName = isEditingPaymentInstruction
        ? "updateFixedIncomePaymentInstruction"
        : "createFixedIncomePaymentInstruction";

      const isTreasuryBill = this.isTreasuryBill();

      const rate =
        rateInputType === "discount"
          ? this.toYieldFromDiscount(data.rate, data.tenure)
          : data.rate;

      const variables = isEditingPaymentInstruction
        ? {
            id: paymentInstructionId,
            assignedToId: data.assignedToId,
            amountsPaid: data.amountsPaid,
            rate,
            tenure: data.tenure,
            receiptUrl: paymentInstructionReceiptUrl,
            ...(isTreasuryBill && {
              transactionType: paymentInstructionTransactionType,
            }),
          }
        : {
            fixedIncomeRequestId: this.state.id,
            assignedToId: data.assignedToId,
            amountsPaid: data.amountsPaid,
            rate,
            tenure: data.tenure,
            receiptUrl: paymentInstructionReceiptUrl,
            ...(isTreasuryBill && {
              transactionType: paymentInstructionTransactionType,
            }),
          };

      const response: AxiosResponse = await request({
        query: mutation,
        variables,
      });

      const { err, mutation: mutationResult } = responseHandler<Response>(
        response,
        mutationName,
      );

      if (err || mutationResult.errors?.length) {
        message.error(
          generateDisplayErrorMessage(err || mutationResult.errors),
        );
        this.setState({ isCreatingPaymentInstruction: false });
        return;
      }

      message.success(
        isEditingPaymentInstruction
          ? "Payment instruction updated successfully!"
          : "Payment instruction created successfully and request state updated!",
      );

      this.setState({
        showPaymentInstructionModal: false,
        selectedRate: undefined,
        selectedTenure: undefined,
        amountsPaid: [0],
        isCreatingPaymentInstruction: false,
        isEditingPaymentInstruction: false,
        paymentInstructionId: undefined,
        paymentInstructionReceiptUrl: undefined,
        paymentInstructionTransactionType: "primary",
      });

      if (this.paymentInstructionReference.current) {
        this.paymentInstructionReference.current.resetFields();
      }

      this.getFixedIncomeRequest();
    } catch (error) {
      message.error(
        `Failed to save payment instruction - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      Sentry.captureException(error);
      this.setState({ isCreatingPaymentInstruction: false });
    }
  };

  handleAmountsPaidChange = (index: number, value: number): void => {
    const newAmountsPaid = [...this.state.amountsPaid];
    newAmountsPaid[index] = value;
    this.setState({ amountsPaid: newAmountsPaid });
  };

  addAmountPaid = (): void => {
    this.setState({
      amountsPaid: [...this.state.amountsPaid, 0],
    });
  };

  removeAmountPaid = (index: number): void => {
    if (this.state.amountsPaid.length > 1) {
      const newAmountsPaid = this.state.amountsPaid.filter(
        (_, i) => i !== index,
      );
      this.setState({ amountsPaid: newAmountsPaid });
    }
  };

  handleOfferSelect = (offerId: string | number): void => {
    const selectedOffer = this.state.offers.find(
      (offer) => offer.id === String(offerId),
    );

    if (selectedOffer) {
      // Offers are quoted as yields, so they must not be converted from a discount rate.
      this.setState({
        selectedRate: selectedOffer.rate,
        selectedTenure: selectedOffer.tenure,
        rateInputType: "yield",
        computedYieldRate: undefined,
      });

      this.paymentInstructionReference.current?.setFieldsValue({
        rate: selectedOffer.rate,
        tenure: selectedOffer.tenure,
      });
    }
  };

  toYieldFromDiscount = (discountRate: number, tenure: number): number => {
    const d = discountRate / 100;
    const t = tenure / 365;
    return parseFloat(((d / (1 - d * t)) * 100).toFixed(4));
  };

  computeYieldPreview = (
    rate?: number | null,
    tenure?: number | null,
  ): number | undefined =>
    this.state.rateInputType === "discount" && rate && tenure
      ? this.toYieldFromDiscount(rate, tenure)
      : undefined;

  handleRateTypeChange = (type: "yield" | "discount"): void => {
    this.setState({
      rateInputType: type,
      selectedRate: undefined,
      computedYieldRate: undefined,
    });
    this.paymentInstructionReference.current?.setFieldsValue({
      rate: undefined,
    });
  };

  handleRateChange = (value: number): void => {
    this.setState({
      selectedRate: value,
      computedYieldRate: this.computeYieldPreview(
        value,
        this.state.selectedTenure,
      ),
    });
  };

  handleTenureChange = (value: number): void => {
    this.setState({
      selectedTenure: value,
      computedYieldRate: this.computeYieldPreview(
        this.state.selectedRate,
        value,
      ),
    });
  };

  handleAddBeneficiary = (): void => {
    if (this.state.beneficiaries.length >= 5) {
      message.warning("Maximum of 5 beneficiaries allowed");
      return;
    }
    this.setState({
      beneficiaries: [
        ...this.state.beneficiaries,
        {
          beneficiaryFullName: "",
          beneficiaryGender: "",
          beneficiaryMaritalStatus: "",
          beneficiaryDateOfBirth: "",
          beneficiaryPlaceOfBirth: "",
          beneficiaryResidentialAddress: "",
          beneficiaryDigitalAddress: "",
          beneficiaryOccupation: "",
          beneficiaryTaxIdentificationNumber: "",
          beneficiaryMobileNumber1: "",
          beneficiaryPercentageOfBenefit: 0,
        },
      ],
    });
  };

  handleRemoveBeneficiary = (index: number): void => {
    if (this.state.beneficiaries.length <= 1) {
      message.warning("At least one beneficiary is required");
      return;
    }
    this.setState({
      beneficiaries: this.state.beneficiaries.filter((_, i) => i !== index),
    });
  };

  handleBeneficiaryFieldChange = (
    index: number,
    field: string,
    value: any,
  ): void => {
    const updated = [...this.state.beneficiaries];
    updated[index] = { ...updated[index], [field]: value };
    this.setState({ beneficiaries: updated });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      id,
      fixedIncomeRequest,
      currentRequestsToProcess,
      showDrawer,
      notes,
      showTicketForm,
      isTicketCreateLoading,
      currentNote,
      showKycModal,
      maritalStatuses,
      availableTransitionStates,
      isReasonLoading,
      showReasonModal,
      selectedTransitionState,
      commentRequired,
      showAllocationCalculator,
      showTBillCalculator,
      calculatedBreakdown,
      showRedemptionModal,
      isRedemptionLoading,
      fixedIncomeRequestReasons,
      employmentStatus,
      showPaymentInstructionModal,
      isCreatingPaymentInstruction,
      isEditingPaymentInstruction,
      rollOver,
      rateInputType,
      computedYieldRate,
      paymentInstructionTransactionType,
      treasuryBillRedemptionType,
      treasuryBillBreakdown,
      treasuryBillBreakdownError,
      isTreasuryBillBreakdownLoading,
    } = this.state;

    const isTreasuryBill = isTreasuryBillProduct(fixedIncomeRequest?.product);
    // Treasury bills can also be redeemed once they are in the maturity notice stage.
    const canCreateRedemption =
      fixedIncomeRequest?.state === "purchased" ||
      (isTreasuryBill && fixedIncomeRequest?.state === "matures_in_two_weeks");
    // Treasury bills reach matures_in_two_weeks a month before maturity.
    const requestStateLabel = (text: string): string =>
      isTreasuryBill
        ? text.replace(/two weeks/gi, (match) =>
            match === "Two Weeks" ? "One Month" : "one month",
          )
        : text;

    // const moveToNextLink = `/fixed-income-requests/${
    //   this.props.location?.state?.requests?.[currentRequestsToProcess + 1]?.id
    // }`;

    const moveToPreviousLink = `/fixed-income-requests/${
      this.props.location?.state?.requests?.[currentRequestsToProcess - 1]?.id
    }`;

    const reasons: SelectOptionsInterface[] = fixedIncomeRequestReasons?.map(
      (reason) => {
        return { id: reason.id, name: reason.description || "" };
      },
    );

    const nextPreviousUI = (
      <Row
        gutter={10}
        style={{
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <Col>
          {currentRequestsToProcess > 0 && (
            <NavLink
              to={{
                pathname: moveToPreviousLink,
                state: {
                  type: "fixed-income",
                  requests: this.props?.location?.state?.requests ?? [],
                },
              }}
            >
              <Tooltip placement="left" title="Previous">
                <LeftOutlined style={{ color: "#1D3764" }} />
              </Tooltip>
            </NavLink>
          )}
        </Col>
        {/* 
        <Col>
          {this.props?.location?.state?.requests &&
            currentRequestsToProcess 
              this.props?.location?.state?.requests?.length - 1 && (
              <NavLink
                to={{
                  pathname: moveToNextLink,
                  state: {
                    type: "fixed-income",
                    requests: this.props?.location?.state?.requests ?? [],
                  },
                }}
              >
                <Tooltip placement="right" title="Next">
                  <RightOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
        </Col> */}

        <Col>
          {this.props?.location?.state?.requests &&
            `${currentRequestsToProcess + 1} / ${
              this.props?.location?.state?.requests?.length
            }`}
        </Col>
      </Row>
    );

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            {ModalCreator({
              title: "Add Comment",
              okType: "primary",
              okText: "Submit",
              showModal: showReasonModal,
              onOkay: this.saveStateReason,
              toggleModal: this.toggleReasonModal,
              isLoading: isReasonLoading,
              okayButtonProps: {
                disabled: isReasonLoading,
              },
              inscription: LoanRequestReasonUI({
                reference: this.reasonReference,
                selectedTransitionState,
                availableTransitionStates,
                isLoading: isReasonLoading,
                commentRequired,
                handleFileUploadChange: this.handleFileUploadChange,
                financialInstitutions: this.state.financialInstitutions,
                handlePaymentMethodSelect: this.handlePaymentMethodSelect,
                institutionOptions: this.state.institutionOptions,
                selectedPaymentMethod: this.state.selectedPaymentMethod,
                reasons,
                handleReasonSelect: this.handleReasonSelect,
                requiresIsin: isTreasuryBill,
              }),
            })}
            <Row>
              <Col span={24}>
                {fixedIncomeRequest && (
                  <Descriptions
                    title={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {fixedIncomeRequest.state !== "pending_kyc" &&
                          fixedIncomeRequest.fixedIncomeRequestKyc?.riskProfile
                            ?.riskLevel && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              <b>Risk Level</b>
                              <Tag
                                color={
                                  fixedIncomeRequest.fixedIncomeRequestKyc
                                    .riskProfile?.riskLevel === "low_risk"
                                    ? "green"
                                    : fixedIncomeRequest.fixedIncomeRequestKyc
                                        .riskProfile?.riskLevel ===
                                      "medium_risk"
                                    ? "orange"
                                    : "red"
                                }
                              >
                                {getRiskLevelDisplay(
                                  fixedIncomeRequest.fixedIncomeRequestKyc
                                    .riskProfile?.riskLevel,
                                )}
                              </Tag>
                            </span>
                          )}
                      </div>
                    }
                    extra={
                      <Row
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {isTreasuryBill && (
                          <Button onClick={this.toggleTBillCalculator}>
                            T-Bill Pricing Calculator
                          </Button>
                        )}
                        {!fixedIncomeRequest?.fixedIncomeRequestKyc && (
                          <Button type="primary" onClick={this.toggleKycModal}>
                            {fixedIncomeRequest.customer
                              ?.lastFixedIncomeRequestKyc
                              ? "Review KYC"
                              : "Upload KYC"}
                          </Button>
                        )}
                        <Button
                          className="base-button"
                          onClick={this.toggleDrawer("notifierRecords")}
                        >
                          Notifications
                        </Button>
                        <Button
                          className="base-button"
                          onClick={this.toggleDrawer("audits")}
                        >
                          Request History
                        </Button>

                        <EntitledSection
                          entitlement={entitlements.FixedIncomeRequests.edit}
                        >
                          <NavLink to={`/fixed-income-requests/${id}/edit`}>
                            <Button
                              icon={<EditOutlined />}
                              className="base-button"
                            >
                              Edit
                            </Button>
                          </NavLink>
                        </EntitledSection>
                        {nextPreviousUI}
                      </Row>
                    }
                  >
                    <Descriptions.Item label={<b>State</b>} key="State">
                      {fixedIncomeRequest.state ? (
                        <Tag
                          color={getStateColor(fixedIncomeRequest.state)}
                          style={{ textTransform: "capitalize" }}
                        >
                          {requestStateLabel(
                            formatSnakeCase(fixedIncomeRequest.state),
                          )}
                        </Tag>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Customer Name</b>}
                      key="Customer"
                    >
                      {fixedIncomeRequest.customer ? (
                        <NavLink
                          to={`/customers/${fixedIncomeRequest.customer.id}`}
                        >
                          {generateNameFromCustomer(
                            fixedIncomeRequest.customer,
                          )}
                        </NavLink>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Petra ID</b>}
                      key="CustomerPetraId"
                    >
                      {fixedIncomeRequest.customer?.petraId || "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Provider</b>} key="Provider">
                      {fixedIncomeRequest.provider ? (
                        <NavLink
                          to={`/providers/${fixedIncomeRequest.provider.id}`}
                        >
                          {fixedIncomeRequest.provider.name}
                        </NavLink>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Product</b>} key="Product">
                      {fixedIncomeRequest.product?.name || "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Source</b>} key="Source">
                      {fixedIncomeRequest.source
                        ? formatSnakeCase(
                            capitalizeFirstLetter(fixedIncomeRequest.source),
                          )
                        : "N/A"}
                    </Descriptions.Item>

                    {fixedIncomeRequest?.sourceRedemption && (
                      <Descriptions.Item label={<b>Source Redemption</b>}>
                        {fixedIncomeRequest?.sourceRedemption ? (
                          <NavLink
                            to={`/fixed-income-redemptions/${fixedIncomeRequest?.sourceRedemption?.id}`}
                          >
                            View Redemption
                          </NavLink>
                        ) : (
                          "N/A"
                        )}
                      </Descriptions.Item>
                    )}

                    {fixedIncomeRequest?.sourceRedemption && (
                      <Descriptions.Item label={<b>Main Request</b>}>
                        {fixedIncomeRequest?.sourceRedemption ? (
                          <NavLink
                            to={`/fixed-income-requests/${fixedIncomeRequest?.sourceRedemption?.fixedIncomeRequest?.id}`}
                          >
                            View Request
                          </NavLink>
                        ) : (
                          "N/A"
                        )}
                      </Descriptions.Item>
                    )}

                        {fixedIncomeRequest?.issuedDate && (
                          <Descriptions.Item
                            label={<b>Issued Date</b>}
                            key="issuedDate"
                          >
                            {fixedIncomeRequest.issuedDate
                              ? moment(
                                  fixedIncomeRequest.issuedDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>
                        )}

                        {fixedIncomeRequest?.customerSubscription
                          ?.brokerageProductId && (
                          <Descriptions.Item
                            label={<b>Brokerage Product ID</b>}
                            key="brokerageProductId"
                          >
                            {
                              fixedIncomeRequest.customerSubscription
                                .brokerageProductId
                            }
                          </Descriptions.Item>
                        )}

                    <Descriptions.Item
                      label={<b>Assigned To</b>}
                      key="AssignedTo"
                    >
                      {fixedIncomeRequest.assignedTo?.name || "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={<b>Request Date</b>}
                      key="createdAt"
                    >
                      {fixedIncomeRequest.createdAt
                        ? moment(
                            fixedIncomeRequest.createdAt,
                            "YYYY-MM-DD HH:mm:ss",
                          ).format("llll")
                        : "N/A"}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </Col>
            </Row>
          </Fragment>
        </Card>

        <div
          style={{
            margin: "25px 0",
            display: "flex",
            justifyContent: "start",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", gap: "5px" }}>
            {availableTransitionStates?.map((transition, i) => (
              <Button
                onClick={() => this.requestForReason(transition.state)}
                className={getStateButtonClass(transition.state)}
                id={`transition-btn-${i}`}
                key={i}
                disabled={isReasonLoading}
              >
                {requestStateLabel(generateStateButtonText(transition.event))}
              </Button>
            ))}
          </div>

          {canCreateRedemption && (
            <Button
              onClick={this.toggleRedemptionModal}
              style={{ borderColor: "blue", color: "blue" }}
            >
              Create Redemption
            </Button>
          )}
        </div>

        {fixedIncomeRequest?.fixedIncomePaymentInstruction && (
          <FixedIncomePaymentInstructionPanel
            paymentInstruction={
              fixedIncomeRequest.fixedIncomePaymentInstruction
            }
            onEdit={() => this.togglePaymentInstructionModal(true)}
          />
        )}

        {fixedIncomeRequest?.allocationBreakdown?.success && (
          <MaturityDetailsCard
            fixedIncomeRequest={fixedIncomeRequest}
            onCalculateBreakdown={this.toggleAllocationCalculator}
          />
        )}

        {showTBillCalculator &&
          ModalCreator({
            title: "T-Bill Pricing Calculator",
            showModal: showTBillCalculator,
            toggleModal: this.toggleTBillCalculator,
            showFooter: false,
            width: 700,
            maskClosable: true,
            inscription: <TBillPricingCalculator />,
          })}

        {showAllocationCalculator &&
          ModalCreator({
            title: "Calculate Allocation Breakdown",
            showModal: showAllocationCalculator,
            toggleModal: this.toggleAllocationCalculator,
            onOkay: this.calculateAllocationBreakdown,
            width: 900,
            okText: "Calculate",
            okType: "primary",
            maskClosable: true,
            inscription: (
              <>
                <AllocationBreakdownCalculator
                  reference={this.allocationCalculatorReference}
                  redeemableAmount={fixedIncomeRequest?.redeemableAmount}
                  maturityDate={fixedIncomeRequest?.maturityDate}
                  issuedDate={fixedIncomeRequest?.issuedDate}
                />
                {calculatedBreakdown && (
                  <CalculatedBreakdownDisplay breakdown={calculatedBreakdown} />
                )}
              </>
            ),
          })}

        {showKycModal &&
          ModalCreator({
            title: fixedIncomeRequest?.customer?.lastFixedIncomeRequestKyc
              ? "Review Previous KYC Information"
              : "Fixed Income Request KYC",
            showModal: showKycModal,
            toggleModal: this.toggleKycModal,
            onOkay: () => {},
            width: 1400,
            showFooter: false,
            inscription:
              fixedIncomeRequest?.customer?.lastFixedIncomeRequestKyc &&
              !fixedIncomeRequest?.fixedIncomeRequestKyc ? (
                <FixedIncomeRequestKycReview
                  lastKyc={
                    fixedIncomeRequest.customer.lastFixedIncomeRequestKyc as any
                  }
                  fixedIncomeRequest={fixedIncomeRequest}
                  customer={fixedIncomeRequest.customer}
                  maritalStatuses={maritalStatuses}
                  onConfirmReview={this.handleKycSubmit}
                  onCancel={this.toggleKycModal}
                  isLoading={isLoading}
                  handleFileUploadChange={this.handleFileUploadChange}
                  handleDelete={this.handleDelete}
                  employmentStatus={employmentStatus}
                  handleEmploymentStatusChange={
                    this.handleEmploymentStatusChange
                  }
                  handleResidentialStatusSelect={
                    this.handleResidentialStatusSelect
                  }
                  residentialStatus={this.state.residentialStatus}
                  financialInstitutions={this.state.financialInstitutions}
                  branchMap={this.state.branchMap}
                  handlePaymentMethodSelect={
                    this.handlePayoutPaymentMethodSelect
                  }
                  selectedPaymentMethod={this.state.selectedPaymentMethod}
                  handlePayoutCurrencySelect={this.handlePayoutCurrencySelect}
                  selectedPayoutCurrency={this.state.selectedPayoutCurrency}
                  handleInvestmentCurrencySelect={
                    this.handleInvestmentCurrencySelect
                  }
                  selectedInvestmentCurrency={
                    this.state.selectedInvestmentCurrency
                  }
                  payoutBranches={this.state.payoutBranches}
                  handlePayoutBankSelect={this.handleKycPayoutBankSelect}
                  payoutInstitutionOptions={this.state.payoutInstitutionOptions}
                  handleBeneficiaryTypeChange={this.handleBeneficiaryTypeChange}
                  beneficiaryType={this.state.beneficiaryType}
                  kycEnumOptions={this.state.kycEnumOptions}
                  beneficiaries={this.state.beneficiaries}
                  onAddBeneficiary={this.handleAddBeneficiary}
                  onRemoveBeneficiary={this.handleRemoveBeneficiary}
                  onBeneficiaryFieldChange={this.handleBeneficiaryFieldChange}
                  kycDeclaration={this.state.kycDeclaration}
                  handleKycDeclarationChange={this.handleKycDeclarationChange}
                  handleStatementFrequencyChange={
                    this.handleStatementFrequencyChange
                  }
                  selectedStatementFrequency={
                    this.state.selectedStatementFrequency
                  }
                />
              ) : (
                <FixedIncomeRequestKycForm
                  fixedIncomeRequest={fixedIncomeRequest}
                  onSubmit={this.handleKycSubmit}
                  onCancel={this.toggleKycModal}
                  isLoading={isLoading}
                  customer={fixedIncomeRequest?.customer}
                  isEditMode={!!fixedIncomeRequest?.fixedIncomeRequestKyc}
                  handleFileUploadChange={this.handleFileUploadChange}
                  handleDelete={this.handleDelete}
                  handleEmploymentStatusChange={
                    this.handleEmploymentStatusChange
                  }
                  handleResidentialStatusSelect={
                    this.handleResidentialStatusSelect
                  }
                  residentialStatus={this.state.residentialStatus}
                  employmentStatus={employmentStatus}
                  financialInstitutions={this.state.financialInstitutions}
                  branchMap={this.state.branchMap}
                  handlePaymentMethodSelect={
                    this.handlePayoutPaymentMethodSelect
                  }
                  selectedPaymentMethod={this.state.selectedPaymentMethod}
                  handlePayoutCurrencySelect={this.handlePayoutCurrencySelect}
                  selectedPayoutCurrency={this.state.selectedPayoutCurrency}
                  handleInvestmentCurrencySelect={
                    this.handleInvestmentCurrencySelect
                  }
                  selectedInvestmentCurrency={
                    this.state.selectedInvestmentCurrency
                  }
                  payoutBranches={this.state.payoutBranches}
                  handlePayoutBankSelect={this.handleKycPayoutBankSelect}
                  payoutInstitutionOptions={this.state.payoutInstitutionOptions}
                  handleBeneficiaryTypeChange={this.handleBeneficiaryTypeChange}
                  beneficiaryType={this.state.beneficiaryType}
                  kycEnumOptions={this.state.kycEnumOptions}
                  beneficiaries={this.state.beneficiaries}
                  onAddBeneficiary={this.handleAddBeneficiary}
                  onRemoveBeneficiary={this.handleRemoveBeneficiary}
                  onBeneficiaryFieldChange={this.handleBeneficiaryFieldChange}
                  kycDeclaration={this.state.kycDeclaration}
                  handleKycDeclarationChange={this.handleKycDeclarationChange}
                  handleStatementFrequencyChange={
                    this.handleStatementFrequencyChange
                  }
                  selectedStatementFrequency={
                    this.state.selectedStatementFrequency
                  }
                />
              ),
            okType: "primary",
            maskClosable: false,
          })}

        {fixedIncomeRequest?.fixedIncomeRequestKyc &&
          fixedIncomeRequest.state && (
            <FixedIncomeRequestKycView
              onEdit={this.toggleKycModal}
              fixedIncomeRequest={fixedIncomeRequest}
            />
          )}

        {fixedIncomeRequest?.fixedIncomePayments &&
          fixedIncomeRequest.fixedIncomePayments.length > 0 && (
            <Collapse style={{ marginTop: 16 }}>
              <Collapse.Panel
                key="payments"
                header={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>
                      Payments{" "}
                      <Badge
                        count={fixedIncomeRequest.fixedIncomePayments.length}
                        style={tabPaneStyle}
                      />
                    </span>
                  </div>
                }
              >
                <Table
                  dataSource={fixedIncomeRequest.fixedIncomePayments}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  summary={(pageData) => {
                    const totalAmount = pageData.reduce(
                      (sum, payment) => sum + (payment.amount || 0),
                      0,
                    );

                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={1}>
                            <b>Total</b>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <b>{formatter.format(totalAmount)}</b>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} colSpan={6} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                >
                  <Table.Column
                    title="Payment Date"
                    dataIndex="paymentDate"
                    key="paymentDate"
                    width="12%"
                    render={(date: string) =>
                      date
                        ? moment(date, "YYYY-MM-DD").format("DD MMM YYYY")
                        : "N/A"
                    }
                  />
                  <Table.Column
                    title="State"
                    dataIndex="state"
                    key="state"
                    width="12%"
                    render={(state: string) => (
                      <Tag
                        color={getStateColor(state)}
                        style={{ textTransform: "capitalize" }}
                      >
                        {state ? formatSnakeCase(state) : "N/A"}
                      </Tag>
                    )}
                  />
                  <Table.Column
                    title="Amount"
                    dataIndex="amount"
                    key="amount"
                    width="12%"
                    render={(amount: number) =>
                      amount ? formatter.format(amount) : "N/A"
                    }
                  />
                  <Table.Column
                    title="Account Name"
                    dataIndex="accountName"
                    key="accountName"
                    width="15%"
                    render={(name: string) => name || "N/A"}
                  />
                  <Table.Column
                    title="Payment Method"
                    dataIndex="paymentMethod"
                    key="paymentMethod"
                    width="15%"
                    render={(name: string) =>
                      name ? formatSnakeCase(name) : "N/A"
                    }
                  />
                  <Table.Column
                    title="Account Number"
                    dataIndex="accountNumber"
                    key="accountNumber"
                    width="13%"
                    render={(number: string) => number || "N/A"}
                  />
                  <Table.Column
                    title="Reference"
                    dataIndex="reference"
                    key="reference"
                    width="15%"
                    render={(ref: string) => ref || "N/A"}
                  />
                  <Table.Column
                    title="Payment Receipt"
                    dataIndex="paymentReceiptUrl"
                    key="paymentReceiptUrl"
                    width="15%"
                    render={(url: string) =>
                      url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          View Receipt
                        </a>
                      ) : (
                        "N/A"
                      )
                    }
                  />
                  <Table.Column
                    title="Recorded On"
                    dataIndex="createdAt"
                    key="createdAt"
                    width="18%"
                    render={(date: string) =>
                      moment(date, "YYYY-MM-DD HH:mm:ss").format(
                        "DD MMM YYYY, HH:mm",
                      )
                    }
                  />
                </Table>
              </Collapse.Panel>
            </Collapse>
          )}

        {fixedIncomeRequest?.fixedIncomeRedemptions &&
          fixedIncomeRequest.fixedIncomeRedemptions.length > 0 && (
            <FixedIncomeRedemptionsTable
              redemptions={fixedIncomeRequest.fixedIncomeRedemptions}
              onRedemptionUpdate={this.getFixedIncomeRequest}
              financialInstitutions={this.state.financialInstitutions}
            />
          )}

        {fixedIncomeRequest && (
          <Collapse style={{ marginTop: 16 }}>
            <Collapse.Panel header="Notes" key="notes">
              <NotesSection
                showHeading={false}
                reference={this.reference}
                createNote={this.createNote}
                notes={notes}
                showForm={true}
                canCreateTicket={true}
                showTicketForm={showTicketForm}
                toggleTicketCreate={this.toggleCreateTicket}
                createTicket={this.createTicket}
                ticketLoading={isTicketCreateLoading}
                ticketReference={this.ticketReference}
                customer={fixedIncomeRequest.customer}
                currentNote={currentNote}
                entity="FixedIncomeRequest"
                onNoteUpdate={this.handleNoteUpdate}
              />
            </Collapse.Panel>
          </Collapse>
        )}

        {showRedemptionModal &&
          ModalCreator({
            title: isTreasuryBill
              ? "Create Treasury Bill Redemption"
              : "Create Fixed Income Redemption",
            showModal: showRedemptionModal,
            toggleModal: this.toggleRedemptionModal,
            onOkay: this.handleRedemptionSubmit,
            width: 600,
            okText: "Create Redemption",
            okType: "primary",
            isLoading: isRedemptionLoading,
            maskClosable: false,
            inscription: (
              <>
                <NewBaseForm
                  layout="vertical"
                  reference={this.redemptionReference}
                  fieldsToHide={
                    isTreasuryBill
                      ? [
                          "assignedToId",
                          "rollOver",
                          // No charges apply once the bill has matured.
                          ...(treasuryBillBreakdown?.timing === "matured"
                            ? ["serviceCostRate"]
                            : []),
                        ]
                      : ["redemptionType", "serviceCostRate"]
                  }
                  fields={[
                    {
                      id: "redemptionType",
                      name: "redemptionType",
                      inputType: "select",
                      rule: "string",
                      required: isTreasuryBill,
                      label: "Redemption Type",
                      placeholder: "Select redemption type",
                      selectOptions: [
                        { id: "full", name: "Full Redemption" },
                        { id: "partial", name: "Partial Redemption" },
                      ],
                      selectHandler: this.handleTreasuryBillRedemptionTypeSelect,
                      colConfig: { span: 24 },
                    },
                    {
                      id: "serviceCostRate",
                      name: "serviceCostRate",
                      inputType: "number",
                      rule: "number",
                      required: isTreasuryBill,
                      label: "Service Cost (%)",
                      placeholder: "Enter service cost rate",
                      initialValue: 10,
                      min: 0,
                      max: 99.99,
                      onNumberChange: this.scheduleTreasuryBillBreakdown,
                      colConfig: { span: 24 },
                      elementStyle: { width: "100%" },
                    },
                    {
                      id: "amount",
                      name: "amount",
                      required: true,
                      inputType: "number",
                      rule: "number",
                      disabled:
                        isTreasuryBill && treasuryBillRedemptionType === "full",
                      label:
                        isTreasuryBill && treasuryBillRedemptionType === "full"
                          ? treasuryBillBreakdown?.timing === "matured"
                            ? "Amount (base plus accrued interest)"
                            : "Amount (after charges)"
                          : "Amount",
                      placeholder: "Enter amount to withdraw",
                      onNumberChange: isTreasuryBill
                        ? this.scheduleTreasuryBillBreakdown
                        : undefined,
                      colConfig: { span: 24 },
                      elementStyle: { width: "100%" },
                    },
                    {
                      id: "rediscountDate",
                      name: "rediscountDate",
                      required: false,
                      inputType: "defaultDate",
                      rule: "object",
                      label: "Rediscount Date",
                      placeholder: "Select rediscount date",
                      dateChangeHandler: isTreasuryBill
                        ? this.scheduleTreasuryBillBreakdown
                        : undefined,
                      elementStyle: { width: "100%" },
                      colConfig: { span: 24 },
                    },
                    {
                      id: "assignedToId",
                      name: "assignedToId",
                      rule: "string",
                      inputType: "search",
                      required: true,
                      label: "Assign To",
                      placeholder: "Search and select advisor",
                      selectOptions: this.state.users,
                      changeHandler: this.handleUserSearchInput,
                      isSearching: this.state.isSearchingUser,
                      colConfig: { span: 24 },
                    },
                    {
                      id: "rollOver",
                      name: "rollOver",
                      inputType: "checkbox",
                      rule: "boolean",
                      required: false,
                      label: "Roll Over Redeeming Amount",
                      checkboxCheckedValue: rollOver,
                      checkboxChangeHandler: (e) =>
                        this.changeRollOver(e.target.checked),
                      colConfig: { span: 24 },
                    },
                    {
                      id: "paymentMethod",
                      name: "paymentMethod",
                      inputType: "select",
                      rule: "string",
                      required: true,
                      disabled: !!rollOver,
                      placeholder: "Select payout method",
                      label: "Payout Method",
                      initialValue:
                        fixedIncomeRequest?.customer.lastFixedIncomeRequestKyc
                          ?.payoutPaymentMethod,
                      selectOptions: paymentMethods,
                      selectHandler: this.handleRedemptionPaymentMethodSelect,
                      colConfig: { span: 24 },
                    },
                    {
                      id: "bankName",
                      name: "bankName",
                      required: true,
                      inputType: "select",
                      rule: "string",
                      disabled: !!rollOver,
                      initialValue: institutionNameFor(
                        fixedIncomeRequest?.customer.lastFixedIncomeRequestKyc
                          ?.payoutBankName,
                        this.state.financialInstitutions,
                      ),
                      label:
                        this.state.selectedRedemptionPaymentMethod ===
                        "mobile_money"
                          ? "Mobile Money Provider"
                          : "Bank Name",
                      placeholder:
                        this.state.selectedRedemptionPaymentMethod ===
                        "mobile_money"
                          ? "Select provider"
                          : "Select bank",
                      selectOptions: this.state.financialInstitutions.map(
                        (institution) => ({
                          ...institution,
                          value: institution.name,
                        }),
                      ),
                      colConfig: { span: 24 },
                    },
                    {
                      id: "accountName",
                      name: "accountName",
                      required: true,
                      inputType: "string",
                      disabled: !!rollOver,
                      rule: "string",
                      label: "Account Name",
                      placeholder: "Enter account name",
                      initialValue:
                        fixedIncomeRequest?.customer.lastFixedIncomeRequestKyc
                          ?.payoutAccountName,
                      colConfig: { span: 24 },
                    },
                    {
                      id: "accountNumber",
                      name: "accountNumber",
                      required: true,
                      disabled: !!rollOver,
                      inputType:
                        this.state.selectedRedemptionPaymentMethod ===
                        "mobile_money"
                          ? "phone"
                          : "string",
                      rule: "string",
                      label:
                        this.state.selectedRedemptionPaymentMethod ===
                        "mobile_money"
                          ? "Mobile Money Number"
                          : "Account Number",
                      placeholder:
                        this.state.selectedRedemptionPaymentMethod ===
                        "mobile_money"
                          ? "Enter mobile money number"
                          : "Enter account number",
                      colConfig: { span: 24 },
                      initialValue:
                        fixedIncomeRequest?.customer.lastFixedIncomeRequestKyc
                          ?.payoutAccountNumber,
                      elementStyle: { width: "100%" },
                    },
                    {
                      name: "supportingDocumentUrl",
                      label: "Upload Supporting Document",
                      inputType: "fileUpload",
                      defaultFileList: [],
                      placeholder: "Upload Supporting Document",
                      rule: "string",
                      required: false,
                      changeHandler: (combinedInfo: string) =>
                        this.handleFileUploadChange(
                          "supportingDocumentUrl",
                          combinedInfo,
                        ),
                      colConfig: { span: 24 },
                    },
                  ]}
                />
                {isTreasuryBill && (
                  <Spin spinning={!!isTreasuryBillBreakdownLoading}>
                    {treasuryBillBreakdownError ? (
                      <Alert
                        type="error"
                        showIcon
                        message={treasuryBillBreakdownError}
                      />
                    ) : (
                      treasuryBillBreakdown && (
                        <TreasuryBillRedemptionBreakdown
                          data={treasuryBillBreakdown}
                          column={{ xs: 1, sm: 2 }}
                        />
                      )
                    )}
                  </Spin>
                )}
              </>
            ),
          })}

        {showPaymentInstructionModal &&
          ModalCreator({
            title: isEditingPaymentInstruction
              ? "Edit Payment Instruction"
              : "Create Payment Instruction",
            showModal: showPaymentInstructionModal,
            toggleModal: () => this.togglePaymentInstructionModal(false),
            onOkay: this.createPaymentInstruction,
            width: 600,
            okText: isEditingPaymentInstruction ? "Update" : "Create",
            okType: "primary",
            isLoading: isCreatingPaymentInstruction,
            maskClosable: false,
            inscription: (
              <>
                {isTreasuryBill && (
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 500, marginRight: 12 }}>
                      Rate type:
                    </span>
                    <Radio.Group
                      value={rateInputType}
                      onChange={(e) =>
                        this.handleRateTypeChange(e.target.value)
                      }
                      optionType="button"
                      buttonStyle="solid"
                    >
                      <Radio.Button value="yield">Yield Rate</Radio.Button>
                      <Radio.Button value="discount">
                        Discount Rate
                      </Radio.Button>
                    </Radio.Group>
                  </div>
                )}
                <NewBaseForm
                  layout="vertical"
                  reference={this.paymentInstructionReference}
                  fieldsToHide={[]}
                  fields={[
                    ...(isTreasuryBill
                      ? ([
                          {
                            id: "transactionType",
                            name: "transactionType",
                            inputType: "select",
                            rule: "string",
                            required: false,
                            label: "Transaction Type",
                            placeholder: "Select transaction type",
                            initialValue: paymentInstructionTransactionType || "primary",
                            selectOptions: [
                              {
                                id: "primary",
                                value: "primary",
                                name: "Primary",
                              },
                              {
                                id: "secondary",
                                value: "secondary",
                                name: "Secondary",
                              },
                            ],
                            selectHandler: (value: string) =>
                              this.setState({
                                paymentInstructionTransactionType: value,
                              }),
                            colConfig: { span: 24 },
                          },
                        ] as FormFieldConfig[])
                      : []),
                    ...(this.state.offers.length > 0
                      ? ([
                          {
                            id: "offerSelector",
                            name: "offerSelector",
                            inputType: "select",
                            rule: "string",
                            required: false,
                            placeholder: "Select an offer",
                            label: "Offers",
                            selectOptions: this.state.offers,
                            selectHandler: this.handleOfferSelect,
                            colConfig: { span: 24 },
                          },
                        ] as FormFieldConfig[])
                      : []),
                    {
                      id: "rate",
                      name: "rate",
                      inputType: "number",
                      rule: "number",
                      required: true,
                      placeholder:
                        rateInputType === "discount"
                          ? "Enter discount rate"
                          : "Enter rate",
                      label:
                        rateInputType === "discount"
                          ? "Discount Rate (%)"
                          : "Rate (%)",
                      initialValue: this.state.selectedRate,
                      onNumberChange: this.handleRateChange,
                      colConfig: { span: 11 },
                      elementStyle: { width: "100%" },
                    },
                    {
                      id: "tenure",
                      name: "tenure",
                      inputType: "number",
                      rule: "number",
                      required: true,
                      placeholder: "Enter tenure",
                      label: "Tenure (days)",
                      initialValue: this.state.selectedTenure,
                      onNumberChange: this.handleTenureChange,
                      colConfig: { span: 11, offset: 1 },
                      elementStyle: { width: "100%" },
                    },
                    {
                      id: "assignedToId",
                      name: "assignedToId",
                      rule: "string",
                      inputType: "search",
                      required: true,
                      label: "Assign To",
                      placeholder: "Search and select advisor",
                      selectOptions: this.state.users,
                      changeHandler: this.handleUserSearchInput,
                      isSearching: this.state.isSearchingUser,
                      colConfig: { span: 24 },
                    },
                    {
                      id: "amountsPaid",
                      name: "amountsPaid",
                      inputType: "numberArray",
                      rule: "array",
                      required: true,
                      label: "Amounts Paid",
                      placeholder: "Enter amounts",
                      colConfig: { span: 24 },
                      elementStyle: { width: "100%" },
                      initialValue: this.state.amountsPaid,
                      onNumberArrayChange: (index: number, value: number) => {
                        const newAmountsPaid = [...this.state.amountsPaid];
                        newAmountsPaid[index] = value;
                        this.setState({ amountsPaid: newAmountsPaid });
                        this.paymentInstructionReference.current?.setFieldsValue(
                          {
                            amountsPaid: newAmountsPaid,
                          },
                        );
                      },
                      onAddNumberArrayItem: () => {
                        const newAmountsPaid = [...this.state.amountsPaid, 0];
                        this.setState({ amountsPaid: newAmountsPaid });
                        this.paymentInstructionReference.current?.setFieldsValue(
                          {
                            amountsPaid: newAmountsPaid,
                          },
                        );
                      },
                      onRemoveNumberArrayItem: (index: number) => {
                        if (this.state.amountsPaid.length > 1) {
                          const newAmountsPaid = this.state.amountsPaid.filter(
                            (_, i) => i !== index,
                          );
                          this.setState({ amountsPaid: newAmountsPaid });
                          this.paymentInstructionReference.current?.setFieldsValue(
                            {
                              amountsPaid: newAmountsPaid,
                            },
                          );
                        }
                      },
                    },
                    {
                      name: "receiptUrl",
                      label: "Upload Payment Receipt",
                      inputType: "fileUpload",
                      defaultFileList: [],
                      placeholder: "Upload Payment Receipt",
                      rule: "string",
                      required: false,
                      changeHandler: (combinedInfo: string) =>
                        this.handleFileUploadChange(
                          "paymentInstructionReceiptUrl",
                          combinedInfo,
                        ),
                      colConfig: { span: 24 },
                    },
                  ]}
                />
                {isTreasuryBill &&
                  rateInputType === "discount" &&
                  computedYieldRate !== undefined && (
                    <div
                      style={{
                        marginTop: -8,
                        marginBottom: 8,
                        color: "#1677ff",
                        fontSize: 13,
                      }}
                    >
                      Computed yield rate: <strong>{computedYieldRate}%</strong>
                    </div>
                  )}
              </>
            ),
          })}

        <Drawer
          title={this.getTitleBasedOnDrawerContent()}
          width={1200}
          onClose={this.toggleDrawer("")}
          visible={showDrawer}
          bodyStyle={{ paddingBottom: 80 }}
          maskClosable={false}
        >
          {this.getDrawerContent()}
        </Drawer>
        <br />
      </Spin>
    );
  }
}
